import { useInboxStore } from '@/entities/inbox';
import { usePhysicianStore } from '@/entities/physician';
import { EVENT_PRIORITY_DROPDOWN_OPTIONS } from '@/shared/config';
import { auth, generateFullName, GETTEXT_ALL_PRIORITY, GETTEXT_ALL_STUDIES } from '@/shared/lib';
import {
  useMergeState,
  useUpdateDeepCompareEffect,
  useUpdateEffect,
} from '@/shared/lib/customHooks';
import _ from 'lodash';
import { useEffect, useMemo, useRef } from 'react';
import { t } from 'ttag';
import { formatSortValue, getSortValueBySortOrder } from '../../lib/inboxSearch';
import type { InboxSearchComponentProps } from './inboxSearchComponent.types';

/**
 * Drives the inbox search bar: search text and field, priority, sort, report status and physician
 * filters, plus the pager. Seeds state from searchGoto, debounces search text by 500 ms, rebuilds
 * the inbox query filter on every filter change and reports it through updateSearchGoto and
 * onChangeSearchComponent, and clears the stored physician when the facility or study type changes.
 *
 * @param props - Search bar props: type, reportType, pageIndex, navigatePage,
 *   onChangeSearchComponent, searchGoto, updateSearchGoto, selectedTypeSeePatients,
 *   handlePreventLoadingInboxDetails and selectedFacility.
 * @returns An object carrying the search state and setter, the store physicians and inbox goto,
 *   the priority and search-by options, and the change, sort, pager and physician handlers.
 */
const useInboxSearchComponent = ({
  type,
  reportType,
  pageIndex,
  navigatePage,
  onChangeSearchComponent,
  searchGoto,
  updateSearchGoto: updateSearchGotoProp,
  selectedTypeSeePatients,
  handlePreventLoadingInboxDetails,
  selectedFacility,
}: Pick<
  InboxSearchComponentProps,
  | 'type'
  | 'reportType'
  | 'pageIndex'
  | 'navigatePage'
  | 'onChangeSearchComponent'
  | 'searchGoto'
  | 'updateSearchGoto'
  | 'selectedTypeSeePatients'
  | 'handlePreventLoadingInboxDetails'
  | 'selectedFacility'
>) => {
  /**
   * The physician list read reactively from the shared physician store, used to build the physician search options. Sourced by selecting the physicians field of the physician Zustand store.
   *
   * @returns The physicians collection held in the store.
   */
  const physiciansRedux = usePhysicianStore((s) => s.physicians);
  /**
   * The whole inbox store slice read reactively, providing the shared search state such as the selected physician value. Sourced from the inbox Zustand store with an identity selector.
   *
   * @returns The inbox store state.
   */
  const inboxesGotoRedux = useInboxStore((s) => s);

  /**
   * The inbox store action that merges updates into the shared goto/search state. Obtained by selecting updateInboxGoto from the inbox Zustand store.
   *
   * @returns The updateInboxGoto action.
   */
  const setStateRedux = useInboxStore((s) => s.updateInboxGoto);

  const isAllReports = type === 'All';

  const priorityOptions = useRef([GETTEXT_ALL_PRIORITY, ...EVENT_PRIORITY_DROPDOWN_OPTIONS]);

  /**
   * The memoized set of search-by options offered in the inbox search bar. Recomputed when the see-patients scope changes: a clinic physician outside the all-studies scope gets only patient name, study ID and patient ID, while every other case also gets the physician option.
   *
   * @returns Array of search-by options with label, value and title.
   */
  const searchByList = useMemo(() => {
    if (auth.isClinicPhysician() && selectedTypeSeePatients?.value !== GETTEXT_ALL_STUDIES) {
      return [
        {
          label: t`Patient name`,
          value: 'patientName',
          title: 'by Patient name',
        },
        {
          label: t`Study ID`,
          value: 'studyFid',
          title: 'by Study ID',
        },
        {
          label: t`Patient ID`,
          value: 'patientId',
          title: 'by Patient ID',
        },
      ];
    }
    return [
      {
        label: t`Patient name`,
        value: 'patientName',
        title: 'by Patient name',
      },
      {
        label: t`Study ID`,
        value: 'studyFid',
        title: 'by Study ID',
      },
      {
        label: t`Patient ID`,
        value: 'patientId',
        title: 'by Patient ID',
      },
      {
        label: t`Physician`,
        value: 'physicianId',
        title: 'by Physician',
      },
    ];
  }, [selectedTypeSeePatients?.value]);
  const searchWithTimeoutFunc = useRef<any>(null);
  const hasChangedSearchValue = useRef(false);
  const hasChangedOptions = useRef(false);
  const filterObject = useRef<any>({
    type: type === 'All' ? undefined : type === 'End of Use' ? 'Study' : type,
    priority: searchGoto.priority || GETTEXT_ALL_PRIORITY,
    patientName: searchGoto.searchByValue === 'patientName' ? searchGoto.searchValue : undefined,
    studyFid: searchGoto.searchByValue === 'studyFid' ? searchGoto.searchValue : undefined,
    patientId: searchGoto.searchByValue === 'patientId' ? searchGoto.searchValue : undefined,
    physicianId:
      searchGoto.searchByValue === 'physicianId'
        ? inboxesGotoRedux.search.physicianSearchValue?.value
        : undefined,
    cursor: undefined,
    sortBy: formatSortValue(searchGoto.sortValue).sortBy,
    sortOrder: formatSortValue(searchGoto.sortValue).sortOrder,
    reportStatus: reportType ? 'Generated' : undefined,
    lastModifiedCursor: undefined,
    statusReports: isAllReports ? searchGoto.statusAllReports : searchGoto.statusReports,
  });

  const [state, setState] = useMergeState<any>({
    priority: searchGoto.priority || GETTEXT_ALL_PRIORITY,
    sortValue: searchGoto.sortValue,
    searchValue: searchGoto.searchValue,
    searchByValue: searchGoto.searchByValue,
    isSmallWidth: window.innerWidth < 1080,
    isOpenFilterPopover: false,
    physicianOptions: [],
    statusReport: isAllReports ? searchGoto.statusAllReports : searchGoto.statusReports,
  });

  /**
   * Mirrors an inbox query filter back into the shared search-goto shape the tabs read. Walks patientName, patientId, studyFid and physicianId and takes the first one set as searchValue plus searchByValue (falling back to searchByValueDefault), adds priority and the sort value rebuilt from sortBy and sortOrder, files statusReports under statusAllReports for the All tab, then hands the result to the updateSearchGoto prop.
   *
   * @param filterObject - The query filter to translate back into search-goto values.
   * @param searchByValueDefault - Search field to record when no search value is set.
   * @returns No return value.
   */
  const updateSearchGoto = (filterObject: any, searchByValueDefault = 'patientName') => {
    const searchGoto: any = {};
    _.forEach(['patientName', 'patientId', 'studyFid', 'physicianId'], (d) => {
      if (filterObject[d]) {
        _.assign(searchGoto, {
          searchValue: filterObject[d],
          searchByValue: d,
        });
        return false;
      }
      _.assign(searchGoto, {
        searchValue: undefined,
      });
      return true;
    });
    if (!searchGoto.searchByValue) {
      _.assign(searchGoto, { searchByValue: searchByValueDefault });
    }
    _.assign(searchGoto, {
      priority: filterObject.priority,
      sortValue: getSortValueBySortOrder(filterObject.sortBy, filterObject.sortOrder),
      ...(isAllReports
        ? { statusAllReports: filterObject.statusReports }
        : { statusReports: filterObject.statusReports }),
    });
    // TODO: Check bug props.updateSearchGoto call from multiple tabs
    updateSearchGotoProp?.(searchGoto);
  };

  /**
   * Builds the inbox query filter from the current search bar state. Clones filterObject, maps the all-priority sentinel to undefined, clears patientName, studyFid, patientId and physicianId and fills only the one matching searchByValue (physicianId taken from the store's physicianSearchValue), then sets sortBy and sortOrder from the sort value and statusReports from the selected status.
   *
   * @returns The newly built inbox query filter object.
   */
  const handleCreateFilterObject = () => {
    const newFilterObject = _.cloneDeep(filterObject.current);
    newFilterObject.priority = state.priority !== GETTEXT_ALL_PRIORITY ? state.priority : undefined;
    _.assign(newFilterObject, {
      patientName: undefined,
      studyFid: undefined,
      patientId: undefined,
      physicianId: undefined,
    });
    if (state.searchByValue === 'patientName') {
      newFilterObject.patientName = state.searchValue?.trim() || undefined;
    } else if (state.searchByValue === 'studyFid') {
      newFilterObject.studyFid = state.searchValue || undefined;
    } else if (state.searchByValue === 'patientId') {
      newFilterObject.patientId = state.searchValue?.trim() || undefined;
    } else if (state.searchByValue === 'physicianId') {
      newFilterObject.physicianId =
        inboxesGotoRedux.search.physicianSearchValue?.value || undefined;
    }
    const sortData = formatSortValue(state.sortValue);
    newFilterObject.sortBy = sortData.sortBy;
    newFilterObject.sortOrder = sortData.sortOrder;
    newFilterObject.statusReports = state.statusReport;
    return newFilterObject;
  };

  /**
   * Stores the physician selected in the inbox search bar into the shared inbox store. Spreads the existing inboxesGotoRedux.search and overwrites only physicianSearchValue through setStateRedux.
   *
   * @param physicianSearchValue - The selected physician option, or null to clear it.
   * @returns No return value.
   */
  const handleSetPhysicianSearchValue = (physicianSearchValue: any) => {
    setStateRedux({
      search: {
        ...inboxesGotoRedux.search,
        physicianSearchValue,
      },
    });
  };

  /**
   * Steps the inbox list back one page from the search bar pager. Calls navigatePage with pageIndex decremented by one.
   *
   * @returns No return value.
   */
  const onClickPrevious = () => {
    navigatePage?.((pageIndex ?? 0) - 1);
  };

  /**
   * Advances the inbox list one page from the search bar pager. Calls navigatePage with pageIndex incremented by one.
   *
   * @returns No return value.
   */
  const onClickNext = () => {
    const newPageIndex = (pageIndex ?? 0) + 1;
    navigatePage?.(newPageIndex);
  };

  /**
   * Applies one field change from the inbox search bar. searchValue collapses whitespace runs and flags hasChangedSearchValue; a changed searchByValue records whether a search value was lost, clears searchValue, closes the filter popover and nulls the stored physician; physician writes the picked value to the store; statusReport also suppresses inbox-detail loading; any other name is set as-is.
   *
   * @param name - The search bar field being changed.
   * @param value - The new value for that field.
   * @returns No return value.
   */
  const onChange = (name: any, value: any) => {
    switch (name) {
      case 'searchValue': {
        hasChangedSearchValue.current = true;
        const searchValue = value?.replace?.(/ |\s|&nbsp;/g, ' ');
        setState({ searchValue });
        break;
      }
      case 'searchByValue': {
        if (value !== state.searchByValue) {
          // *: Refetching data if change searchByValue and has searchValue
          hasChangedOptions.current = !!state.searchValue;
          setState({
            searchByValue: value,
            searchValue: null,
            isOpenFilterPopover: false,
          });
          handleSetPhysicianSearchValue(null);
        } else {
          setState({ isOpenFilterPopover: false });
        }
        break;
      }
      case 'physician': {
        handleSetPhysicianSearchValue(value);
        break;
      }
      case 'statusReport': {
        setState({ [name]: value });
        handlePreventLoadingInboxDetails?.();
        break;
      }
      default: {
        setState({ [name]: value });
        break;
      }
    }
  };

  /**
   * Records the sort option picked in the inbox search bar. Writes sortValue into state.
   *
   * @param sortValue - The selected sort option.
   * @returns No return value.
   */
  const onChangeSort = (sortValue: any) => {
    setState({ sortValue });
  };

  useEffect(() => {
    const filterObject: any = {
      priority: searchGoto.priority || GETTEXT_ALL_PRIORITY,
      patientName: searchGoto.searchByValue === 'patientName' ? searchGoto.searchValue : undefined,
      studyFid: searchGoto.searchByValue === 'studyFid' ? searchGoto.searchValue : undefined,
      patientId: searchGoto.searchByValue === 'patientId' ? searchGoto.searchValue : undefined,
      physicianId:
        searchGoto.searchByValue === 'physicianId'
          ? inboxesGotoRedux.search.physicianSearchValue?.value
          : undefined,
      cursor: undefined,
      sortBy: formatSortValue(searchGoto.sortValue).sortBy,
      sortOrder: formatSortValue(searchGoto.sortValue).sortOrder,
      statusReports: isAllReports ? searchGoto.statusAllReports : searchGoto.statusReports,
    };
    _.assign((filterObject as any).current, filterObject);
    setState({
      priority: searchGoto.priority || GETTEXT_ALL_PRIORITY,
      sortValue: searchGoto.sortValue,
      searchValue: searchGoto.searchByValue !== 'physicianId' ? searchGoto.searchValue : undefined,
      searchByValue: searchGoto.searchByValue,
      statusReport: isAllReports ? searchGoto.statusAllReports : searchGoto.statusReports,
    });
  }, [searchGoto]);

  useEffect(() => {
    /**
     * The physician dropdown options derived from the physicians held in the shared store. Each physician becomes an option whose label is the generated full name and whose value is the physician id.
     *
     * @returns Array of physician options with label and value.
     */
    const physicianOptions = _.map(physiciansRedux, (physician) => ({
      label: generateFullName(physician),
      value: physician.id,
    }));
    setState({ physicianOptions });
  }, [physiciansRedux]);

  useEffect(() => {
    /**
     * Tracks whether the inbox search bar must fall back to its narrow layout. Flips state.isSmallWidth when window.innerWidth crosses 1080 pixels, and only when the flag does not already match.
     *
     * @returns No return value.
     */
    const handleResizeWindow = () => {
      if (window.innerWidth < 1080 && !state.isSmallWidth) {
        setState({ isSmallWidth: true });
      }
      if (window.innerWidth >= 1080 && state.isSmallWidth) {
        setState({ isSmallWidth: false });
      }
    };
    window.addEventListener('resize', handleResizeWindow);
    return () => {
      window.removeEventListener('reisize', handleResizeWindow);
    };
  }, [state.isSmallWidth]);

  // *: Refetching data after changing search value
  useUpdateEffect(
    () => {
      if (hasChangedSearchValue.current) {
        hasChangedSearchValue.current = false;
        if (searchWithTimeoutFunc.current) {
          clearTimeout(searchWithTimeoutFunc.current);
          searchWithTimeoutFunc.current = null;
        }
        searchWithTimeoutFunc.current = setTimeout(() => {
          const filterObject = handleCreateFilterObject();
          updateSearchGoto(filterObject, state.searchByValue);
          onChangeSearchComponent?.(filterObject);
        }, 500);
      }
    },
    [state.searchValue],
    () => {
      if (searchWithTimeoutFunc.current) {
        clearTimeout(searchWithTimeoutFunc.current);
        searchWithTimeoutFunc.current = null;
      }
    },
  );

  useUpdateEffect(() => {
    if (!state.isOpenFilterPopover && hasChangedOptions.current) {
      hasChangedOptions.current = false;
      const filterObject = handleCreateFilterObject();
      updateSearchGoto(filterObject, state.searchByValue);
      onChangeSearchComponent?.(filterObject);
    }
  }, [state.isOpenFilterPopover]);

  useUpdateEffect(() => {
    const filterObject = handleCreateFilterObject();
    updateSearchGoto(filterObject, state.searchByValue);
    onChangeSearchComponent?.(filterObject);
  }, [state.priority, state.sortValue, inboxesGotoRedux.search.physicianSearchValue]);

  useUpdateEffect(() => {
    handleSetPhysicianSearchValue(null);
  }, [selectedFacility]);

  useUpdateEffect(() => {
    if (
      selectedTypeSeePatients?.value !== GETTEXT_ALL_STUDIES &&
      state.searchByValue === 'physicianId'
    ) {
      setState({
        searchByValue: searchByList[0].value,
      });
      handleSetPhysicianSearchValue(null);
    }
  }, [selectedTypeSeePatients?.value]);

  useUpdateDeepCompareEffect(() => {
    const filterObject = handleCreateFilterObject();
    updateSearchGoto(filterObject, state.searchByValue);
    onChangeSearchComponent?.(filterObject);
  }, [state.statusReport]);

  return {
    state,
    setState,
    physiciansRedux,
    inboxesGotoRedux,
    priorityOptions,
    searchByList,
    onChange,
    onChangeSort,
    onClickPrevious,
    onClickNext,
    handleSetPhysicianSearchValue,
  };
};

export { useInboxSearchComponent };
